import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { getFirebaseAuth, getFirebaseDb, getFirebaseStorage } from '../firebase/config';
import { snapDocsToArray } from './firestoreSync';

function messagesCollection(projectId) {
  const db = getFirebaseDb();
  return collection(doc(db, 'projects', String(projectId)), 'messages');
}

function chatReadsCollection(uid) {
  const db = getFirebaseDb();
  return collection(doc(db, 'users', String(uid)), 'chatReads');
}

export function subscribeProjectMessages(projectId, callback, onError) {
  const q = query(messagesCollection(projectId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      const rows = snapDocsToArray(snap).map((m) => ({
        id: m.id,
        senderUid: m.senderUid,
        senderId: m.senderUid,
        sender: m.senderName || 'User',
        own: false,
        text: m.text || '',
        attachmentUrl: m.attachmentUrl || '',
        attachmentName: m.attachmentName || '',
        time: m.timeLabel || '',
        createdAt: m.createdAt,
      }));
      callback(rows);
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

export function subscribeChatReads(uid, callback, onError) {
  if (!uid) return () => {};
  return onSnapshot(
    chatReadsCollection(uid),
    (snap) => {
      const reads = {};
      snap.docs.forEach((d) => {
        reads[d.id] = d.data();
      });
      callback(reads);
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

export async function markProjectChatRead(projectId, uid) {
  if (!projectId || !uid) return;
  const db = getFirebaseDb();
  await setDoc(
    doc(db, 'users', String(uid), 'chatReads', String(projectId)),
    { lastReadAt: serverTimestamp() },
    { merge: true }
  );
}

export async function uploadChatAttachment(projectId, file) {
  const uid = getFirebaseAuth().currentUser?.uid;
  if (!uid) throw new Error('You must be signed in to upload a file.');
  const safeName = (file.name || 'file').replace(/[^\w.\-()+]/g, '_');
  const path = `project-chat/${String(projectId)}/${uid}_${Date.now()}_${safeName}`;
  const storageRef = ref(getFirebaseStorage(), path);
  await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(storageRef);
  return { url, name: file.name || safeName };
}

export async function sendProjectMessage(
  projectId,
  { senderUid, senderName, text, attachmentUrl, attachmentName }
) {
  const now = new Date();
  const timeLabel = now.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  const body = {
    senderUid,
    senderName,
    text: (text || '').trim(),
    createdAt: serverTimestamp(),
    timeLabel,
  };
  if (attachmentUrl) {
    body.attachmentUrl = attachmentUrl;
    body.attachmentName = attachmentName || 'Attachment';
  }
  if (!body.text && !body.attachmentUrl) {
    throw new Error('Message text or attachment is required.');
  }
  await addDoc(messagesCollection(projectId), body);
}

/** Compare Firestore Timestamp or millis to last read marker. */
export function isChatUnread({ project, readEntry, userUid }) {
  if (!project?.lastChatAt || !userUid) return false;
  if (project.lastChatSenderUid === userUid) return false;
  if (!readEntry?.lastReadAt) return true;

  const chatMs = project.lastChatAt?.toMillis?.()
    ?? (typeof project.lastChatAt === 'number' ? project.lastChatAt : 0);
  const readMs = readEntry.lastReadAt?.toMillis?.()
    ?? (typeof readEntry.lastReadAt === 'number' ? readEntry.lastReadAt : 0);
  if (!chatMs) return false;
  return chatMs > readMs;
}

export function countUnreadMessages(messages, readEntry, userUid) {
  if (!messages?.length || !userUid) return 0;
  const readMs = readEntry?.lastReadAt?.toMillis?.()
    ?? (typeof readEntry?.lastReadAt === 'number' ? readEntry.lastReadAt : 0);

  return messages.filter((m) => {
    if (m.senderUid === userUid) return false;
    const msgMs = m.createdAt?.toMillis?.()
      ?? (typeof m.createdAt === 'number' ? m.createdAt : 0);
    if (!readMs) return true;
    return msgMs > readMs;
  }).length;
}
